import React from "react";
import { styled } from "@linaria/react";
import Menu from "../Menu";
import { color, easingBounce, easingBounceMs, fonts, radius, zIndex } from "..";
import Link from "../Link";
import useFilteredOptions from "../Select/useFilteredOptions";
import t from "../i18n";
import Tick from "../Tick";
import Badge from "../Badge";
import Text from "../Text";
import classNames from "classnames";
import { OptionV2 } from "../Option/v2";
import { Tag } from "../Tag";
import { spacings } from "../Theme";

const StyledTick = styled(Tick)`
  margin-left: auto;
`;

const StyledMenu = styled(Menu)<{ width?: number }>`
  z-index: ${zIndex.menu};
  max-height: 350px;
  width: ${props => (props.width === undefined ? "auto" : `${props.width}px`)};
  /* overflow: auto; */
`;

const OptionText = styled.div<{ maxWidth?: number }>`
  max-width: ${props => (props.maxWidth ? `${props.maxWidth - 40}px` : "450px")};
  overflow: hidden;
  text-overflow: ellipsis;
`;

const OptionBadge = styled.div`
  display: flex;
  gap: ${spacings.xs};
  margin-left: 7px;
  align-items: center;
  font-weight: 600;
`;

const SelectLink = styled(Link)`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 26px;
  color: ${color.carbon800};
  font: ${fonts.body2};
  white-space: nowrap;
  padding: 4px 8px;
  border-radius: ${radius};
  background-color: ${color.titanium100};
  box-shadow: inset 0 0 0 1px ${color.titanium100};

  :hover {
    background-color: ${color.titanium200};
  }

  &.opened {
    box-shadow: inset 0 0 0 1px ${color.titanium200};
    :hover {
      background-color: ${color.titanium200};
    }
  }

  ::after {
    margin-left: 10px;
    display: inline-block;
    content: "";
    width: 0.55em;
    height: 0.55em;
    border-top: 1px solid currentColor;
    border-right: 1px solid currentColor;
    transition: transform ${easingBounceMs}ms ${easingBounce};
    transform: rotate(135deg);
    transform-origin: 65% 35%;
    margin-right: 0.3em;
  }
  &.opened::after {
    transform: rotate(315deg);
  }

  &.hasSelectedOptions {
    background-color: ${color.lightNitrogen50};
    box-shadow: inset 0 0 0 1px ${color.lightNitrogen500_Main};
    :hover {
      background-color: ${color.lightNitrogen100};
    }

    &.opened {
      box-shadow: inset 0 0 0 1px ${color.lightNitrogen500_Main};
      :hover {
        background-color: ${color.lightNitrogen100};
      }
    }
  }
`;

const BlankState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 8px 10px;
  flex: 1;
  font-weight: 600;
`;

const StyledBadge = styled(Badge)`
  margin-left: 12px;
`;

export interface FilterOption<T extends string | null> {
  label: string;
  value: T;
  badge?: string;
  description?: string;
}
const getLabel = <T extends string | null>(option: FilterOption<T>) => option.label;

interface FilterSelectsProps<T extends string | null> extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  options: FilterOption<T>[];
  values: T[];
  setValues: (values: T[]) => void;
  filtered?: boolean;
  menuStyle?: React.CSSProperties;
  readOnly?: boolean;
  showTag?: boolean;
  tagLabel?: string;
  onFilter?: (filter: string) => void;
  onScroll?: (e: React.UIEvent<HTMLDivElement>) => void;
  width?: number;
}

function FilterSelect<T extends string | null>({
  className,
  title,
  options,
  values,
  setValues,
  filtered = false,
  menuStyle,
  readOnly,
  showTag,
  tagLabel,
  onFilter,
  onScroll,
  width,
}: FilterSelectsProps<T>) {
  const { setFilter, filteredOptions, filter } = useFilteredOptions(
    options,
    filtered ? getLabel : undefined,
  );

  const handleFilterChange = (filter: string) => {
    setFilter(filter);
    if (onFilter) {
      onFilter(filter);
    }
  };

  const displayOptions = onFilter ? options : filteredOptions;

  const formattedTitle = React.useMemo(() => {
    if (values.length === 0) {
      return title;
    }

    const sortedValues = [...values].sort((a, b) => String(a).localeCompare(String(b)));
    const firstValue = sortedValues[0];
    const firstOption = options.find(o => o.value === firstValue);

    if (!firstOption) {
      return title;
    }

    return (
      <>
        <span>{t("filter.title", title)}</span>
        <Text type="body2SemiBold">
          {firstOption.label}
          {values.length > 1 && `, +${values.length - 1}`}
        </Text>
      </>
    );
  }, [values, options, title]);

  return (
    <StyledMenu
      style={menuStyle}
      arrowWidth={0}
      placement="bottom-start"
      filtered={filtered}
      onFilterChange={handleFilterChange}
      filterValue={filter}
      onScroll={onScroll}
      width={width}
      overlay={
        displayOptions.length ? (
          displayOptions.map(option => {
            const checked = values.includes(option.value);
            return (
              <OptionV2
                disabled={readOnly}
                key={String(option.value)}
                onClick={() => {
                  setValues(
                    checked ? values.filter(v => v !== option.value) : [...values, option.value],
                  );
                }}
              >
                <OptionText slot="text" maxWidth={width}>
                  {option.label}
                </OptionText>
                {option?.description && option.description.length > 0 && (
                  <OptionText maxWidth={width} slot="description">
                    {option.description}
                  </OptionText>
                )}
                <OptionBadge slot="extra">
                  {option?.badge && option.badge.length > 0 ? <Tag>{option.badge}</Tag> : null}
                  {checked ? <StyledTick /> : null}
                </OptionBadge>
              </OptionV2>
            );
            // return (
            //   <Option
            //     disabled={readOnly}
            //     key={String(option.value)}
            //     onClick={() => {
            //       setValues(
            //         checked ? values.filter(v => v !== option.value) : [...values, option.value],
            //       );
            //     }}
            //   >
            //     <OptionTitle>
            //       <OptionText maxWidth={width}>{option.label}</OptionText>
            //       <br />
            //       {option?.description && <OptionText maxWidth={width}>{option.description}</OptionText>}
            //       {option?.badge && option.badge.length > 0 ? (
            //         <OptionBadge>{option.badge}</OptionBadge>
            //       ) : null}
            //     </OptionTitle>
            //     {checked ? <StyledTick /> : null}
            //   </Option>
            // );
          })
        ) : (
          <BlankState>{t("filter.none")}</BlankState>
        )
      }
    >
      <SelectLink className={classNames(className, { hasSelectedOptions: values.length > 0 })}>
        {formattedTitle}
        {tagLabel && showTag && <StyledBadge>{tagLabel}</StyledBadge>}
      </SelectLink>
    </StyledMenu>
  );
}

export default FilterSelect;
